import React, { useState, useEffect } from 'react';
import { Layout, Menu, Breadcrumb, Avatar, Dropdown, theme, Space, Typography } from 'antd';
import {
    UserOutlined,
    LogoutOutlined,
    MenuUnfoldOutlined,
    MenuFoldOutlined,
    DashboardOutlined,
    SettingOutlined,
    TeamOutlined,
    PhoneOutlined,
    FileTextOutlined,
    SafetyCertificateOutlined,
    GlobalOutlined
} from '@ant-design/icons';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import * as Icon from '@ant-design/icons';
import Softphone from '../components/Softphone';
import stcLogo from '../assets/stc-logo-300x316.png';

const { Header, Content, Footer, Sider } = Layout;
const { Text } = Typography;

// Helper to dynamically load icons
const getIcon = (iconName) => {
    if (!iconName) return null;
    const IconComponent = Icon[iconName] || Icon.AppstoreOutlined;
    return <IconComponent />;
};

const MainLayout = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [menuItems, setMenuItems] = useState([]);
    const { logout, user, permissions } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    // Fetch dynamic navigation on mount
    useEffect(() => {
        const fetchNav = async () => {
            try {
                const response = await authService.getNavigation();
                if (response.status === 'success') {
                    const rawItems = response.data;
                    const formattedItems = buildMenuTree(rawItems);
                    setMenuItems(formattedItems);

                    // Update document title based on current path
                    const currentItem = rawItems.find(item => item.route === location.pathname);
                    const moduleName = currentItem ? currentItem.title : 'Admin';
                    document.title = `Admin | ${moduleName} | Save the Children en Perú`;
                }
            } catch (error) {
                console.error("Error fetching navigation", error);
            }
        };
        fetchNav();

        // Ping on route change
        authService.pingSession();

    }, [location.pathname]); // Re-fetch or re-calc if needed


    const hasPermission = (requiredPermission) => {
        if (!requiredPermission) return true;
        return Array.isArray(permissions) && permissions.includes(requiredPermission);
    };

    // Convert flat list to tree with permission filtering
    const buildMenuTree = (items, parentId = null) => {
        return items
            .filter(item => item.parent_id === parentId) // Get children of current parent
            .filter(item => hasPermission(item.required_permission)) // Filter by permission
            .map(item => {
                const children = buildMenuTree(items, item.id);

                // If item has no route and no accessible children, don't show it (unless it's a leaf node designed to show something)
                // But usually permission filter handles visibility. 
                // However, folders with no children due to permissions should probably be hidden.
                if (!item.route && children.length === 0) {
                    return null;
                }

                const hasChildren = children.length > 0;

                return {
                    key: item.route || String(item.id),
                    icon: getIcon(item.icon),
                    label: (item.route && !hasChildren) ? (
                        <Link to={item.route}>{item.title}</Link>
                    ) : (
                        <span style={{ color: '#fff', fontWeight: 400 }}>{item.title}</span>
                    ),
                    children: hasChildren ? children : null,
                };
            })
            .filter(Boolean); // Remove nulls
    };

    const userMenu = {
        items: [
            {
                key: '1',
                label: 'Perfil',
                icon: <UserOutlined />,
                onClick: () => navigate('/profile')
            },
            {
                key: '2',
                label: 'Cerrar Sesión',
                icon: <LogoutOutlined />,
                onClick: logout
            }
        ]
    };

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Sider
                trigger={null}
                collapsible
                collapsed={collapsed}
                width={260}
                style={{
                    background: '#000000',
                    boxShadow: '4px 0 10px rgba(0, 0, 0, 0.1)',
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    zIndex: 100,
                }}
            >
                <div style={{
                    height: 64,
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#000000',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                }}>
                    {collapsed ? (
                        <div style={{ color: '#fff', fontSize: '20px', fontWeight: 'bold' }}>STC</div>
                    ) : (
                        <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold', letterSpacing: '1px' }}>ADMINISTRACIÓN</div>
                    )}
                </div>
                <Menu
                    theme="dark"
                    mode="inline"
                    selectedKeys={[location.pathname]}
                    items={menuItems}
                    style={{ borderRight: 0, marginTop: 16 }}
                />
            </Sider>
            <Layout style={{ marginLeft: collapsed ? 80 : 260, transition: 'all 0.2s' }}>
                <Header style={{
                    padding: 0,
                    background: '#fff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingRight: 24,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 99,
                    width: '100%'
                }}>
                    {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
                        className: 'trigger',
                        onClick: () => setCollapsed(!collapsed),
                        style: { fontSize: '18px', padding: '0 24px', cursor: 'pointer', color: '#555' }
                    })}

                    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                        <Space size="small">
                            <Text type="secondary" style={{ fontSize: '12px' }}>Bienvenido,</Text>
                            <Text strong style={{ color: '#333' }}>{user?.name || 'Usuario'}</Text>
                        </Space>
                        <Dropdown menu={userMenu} placement="bottomRight" arrow>
                            <Avatar
                                style={{
                                    background: '#fff',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                    padding: '4px'
                                }}
                                src={stcLogo}
                                size="large"
                            />
                        </Dropdown>
                    </div>
                </Header>
                <Content style={{ margin: '24px 24px 0', overflow: 'initial' }}>
                    <div
                        style={{
                            padding: 32,
                            minHeight: 360,
                            background: '#fff',
                            borderRadius: 12,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.02)'
                        }}
                    >
                        <Outlet />
                    </div>
                </Content>
                <Footer style={{ textAlign: 'center', color: '#bfbfbf', padding: '24px 50px' }}>
                    Save The Children Admin Portal - Power By ISP CORP ©2026
                </Footer>
            </Layout>
            <Softphone />
        </Layout>

    );
};

export default MainLayout;
